import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  css,
  styled,
} from "@mui/material";
import { useProduct } from "../store/product";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import IconClose from "@mui/icons-material/Close";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { useAppDrawer } from "../store/app-drawer";
import Price from "./Price";
import SquareMeters from "./SquareMeters";
import { useCropperStore } from "../store/cropper/cropper-store";
import Scrollable from "./Scrollable";

const ClearButton = styled(IconButton)(
  ({ theme }) => css`
    position: absolute;
    right: ${theme.spacing(1)};
    top: 50%;
    transform: translateY(-50%);
  `
);

const SearchField = styled(TextField)(
  ({ theme }) => css`
    input {
      padding-right: ${theme.spacing(6)};
    }
  `
);

const MediaButton = styled(Button, {
  shouldForwardProp: (prop) => !["isSelected"].includes(prop),
})(
  ({ theme, isSelected }) => css`
    justify-content: start;
    text-align: left;
    padding-left: ${theme.spacing(3)} !important;
    padding-right: ${theme.spacing(3)} !important;
    border-radius: 0;
    width: 100%;

    ${isSelected &&
    css`
      &,
      &:hover {
        background-color: ${theme.palette.common.black};
        color: ${theme.palette.common.white};
      }
    `}
  `
);

const MediaDescription = styled("div")`
  font-size: 0.8em;
  opacity: 0.8;
`;

const SelectedMediaIcon = ({ isSelected }) => {
  return isSelected ? (
    <CheckIcon sx={{ stroke: "currentColor", strokeWidth: "2px" }} />
  ) : (
    <AddIcon />
  );
};

function CanvasMediaDrawer() {
  const { closeDrawer } = useAppDrawer();
  const { preset, updatePreset } = useCropperStore();
  const { product } = useProduct();
  const searchRef = useRef();
  const [shownMedia, setShownMedia] = useState(mapMediaToSelected());

  function mapMediaToSelected(medias) {
    return (medias || product.media).map((media, index) => ({
      isSelected: media.sku === preset.media,
      ...media,
    }));
  }

  const getSearchIsFilled = () =>
    searchRef.current && searchRef.current.querySelector("input").value !== "";

  const onChangeSearchField = (e) => {
    e.preventDefault();
    const value = e.target.value.trim();
    const results = product?.media?.filter((v) => {
      if (v["name"]) {
        return v["name"].toLowerCase().indexOf(value.toLowerCase()) >= 0;
      }
      return false;
    });

    setShownMedia(mapMediaToSelected(results));
  };

  const clearSearchField = (e) => {
    if (searchRef.current) {
      const input = searchRef.current.querySelector("input");
      input.value = "";

      setShownMedia(mapMediaToSelected());
    }
  };

  const onClickMediaButton = (sku) => {
    updatePreset({ media: String(sku) });
  };

  const checkNextIsSelected = (index) => {
    if (index + 1 < shownMedia.length) {
      return shownMedia[index + 1].sku === preset.media;
    }
    return false;
  };

  const getSelectedMediaName = () => {
    const selected = product.media.filter(({ sku }) => sku === preset.media)[0];
    if (selected) {
      return selected.name;
    }
    return null;
  };

  useEffect(() => {
    setShownMedia(mapMediaToSelected());
  }, [preset.media]);

  return (
    <Stack sx={{ height: "100%", boxSizing: "border-box" }}>
      
      {/* header */}
      <Stack direction={"row"} gap={2} p={2} pb={0} alignItems={'center'}>
        <Button startIcon={<IconClose />} onClick={closeDrawer} size="small">
          Chiudi
        </Button>
        <Typography sx={{ color: "text.secondary" }}>
          Materiali di Stampa
        </Typography>
      </Stack>

      {/* search */}
      <Stack m={2} position={'relative'}>
        <SearchField
          ref={searchRef}
          size="small"
          placeholder="Cerca..."
          onChange={onChangeSearchField}
        />
        <ClearButton
          disabled={!getSearchIsFilled()}
          onClick={clearSearchField}
          size="small">
          <IconClose />
        </ClearButton>
      </Stack>

      {/* list */}
      <Scrollable
        sx={{
          flex: 1,
          height: "100%",
          mb: (theme) => theme.spacing(2),
        }}>
        <Box>
          {shownMedia.map(({
            isSelected, 
            name: mediaName,
            desc: mediaDesk,
            sku: mediaSku 
          }, index) => (
              <React.Fragment key={index}>
                <MediaButton
                  isSelected={isSelected}
                  onClick={() => onClickMediaButton(mediaSku)}>
                  <SelectedMediaIcon isSelected={isSelected} />
                  <Stack sx={{ pl: 2 }}>
                    <Typography>{mediaName}</Typography>
                    <MediaDescription
                      dangerouslySetInnerHTML={{ __html: mediaDesk }}
                    />
                  </Stack>
                </MediaButton>
                {index < shownMedia.length - 1 && (
                  <Divider
                    sx={{
                      opacity: isSelected || checkNextIsSelected(index) ? 0 : 1,
                    }}
                  />
                )}
              </React.Fragment>
            )
          )}
        </Box>
      </Scrollable>

      {/* footer */}
      <Box>
        <DrawerContinueButton 
          onClick={closeDrawer} 
          selectedMedia={getSelectedMediaName()}
        />
      </Box>
    </Stack>
  );
}

function DrawerContinueButton({ onClick, selectedMedia }) {
  return (
    <Button
      variant={"contained"}
      color={"success"}
      endIcon={
        <CheckIcon
          sx={{ stroke: "currentColor", strokeWidth: "2px", fontSize: "1.5em" }}
        />
      }
      onClick={onClick}
      sx={{
        width: '100%',
        padding: 2,
        borderRadius: 0,
        placeContent: "start",
        textAlign: "left",
      }}>
      <Stack width={'100%'}>
        <Stack
          direction={"row"}
          alignItems={"baseline"}
          sx={{
            opacity: 0.6,
            fontWeight: 400,
            fontSize: "1em",
          }}>
          <Typography sx={{ pr: 2 }}>Riepilogo</Typography>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <SquareMeters />
            <ArrowRightAltIcon />
            <Price />
          </Stack>
        </Stack>
        <Typography sx={{ fontSize: "1em" }}>
          {selectedMedia}
        </Typography>
      </Stack>
    </Button>
  );
}

export default CanvasMediaDrawer;
